import React, { useState, useEffect } from 'react';
import { 
  PhotoIcon, 
  XMarkIcon, 
  CheckIcon, 
  CloudArrowUpIcon
} from '@heroicons/react/24/outline';
import { listMediaAssets, uploadMediaFiles, formatFileSize } from '../../services/mediaService';
import Button from '../common/Button';
import Loader from '../common/Loader';

const MediaSelector = ({ 
  isOpen, 
  onClose, 
  onSelectMedia, 
  selectedMediaIds = [], 
  maxSelection = 6,
  title = "Select Package Images",
  category = "packages"
}) => {
  const [mediaAssets, setMediaAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showUploadSection, setShowUploadSection] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Simple fetch function to get all media assets at once
  const fetchMediaAssets = async () => {
    if (!isOpen) return;
    
    try {
      setLoading(true);
      const filters = { 
        category: category,
        asset_type: 'image'
      };
      
      // Fetch all images without pagination (use a very high limit)
      const response = await listMediaAssets(filters, 1, 10000);
      
      if (response && response.media_assets) {
        setMediaAssets(response.media_assets);
      }
    } catch (error) {
      console.error('Error fetching media assets:', error);
    } finally {
      setLoading(false);
    }
  };

  // Only fetch when modal first opens
  useEffect(() => {
    if (isOpen) {
      fetchMediaAssets();
      setSelectedItems(selectedMediaIds || []); // Initialize selection
      
      // Scroll to top of page when modal opens to ensure it's visible
      document.body.style.overflow = 'hidden'; // Prevent background scroll
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Re-enable scrolling when modal closes
      document.body.style.overflow = 'unset';
    }
    
    // Cleanup function to re-enable scrolling if component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSelectItem = (mediaId) => {
    setSelectedItems(prev => {
      if (prev.includes(mediaId)) {
        // Deselect
        return prev.filter(id => id !== mediaId);
      } else {
        // Select (if under limit)
        if (prev.length < maxSelection) {
          return [...prev, mediaId];
        }
        return prev;
      }
    });
  };

  const handleConfirmSelection = () => {
    const selectedMedia = mediaAssets.filter(asset => selectedItems.includes(asset.id));
    // Add flag to indicate these are from media library
    const selectedMediaWithFlag = selectedMedia.map(asset => ({
      ...asset,
      isFromMediaLibrary: true
    }));
    console.log('📤 Selected media from library:', selectedMediaWithFlag);
    onSelectMedia(selectedMediaWithFlag);
    onClose();
  };

  const handleUploadComplete = async (files) => {
    try {
      setUploading(true);
      
      // Import image compression if not already imported
      const imageCompression = (await import('browser-image-compression')).default;
      
      // Compression configuration (same as FileUploader)
      const DEFAULT_COMPRESSION_OPTIONS = {
        maxSizeMB: 0.3,
        maxWidthOrHeight: 1500,
        useWebWorker: true,
        quality: 0.85,
        preserveExif: false,
        initialQuality: 0.9,
        alwaysKeepResolution: false,
        fileType: 'image/jpeg'
      };
      
      // Compress files before upload
      const compressedFiles = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        if (file.type.startsWith('image/')) {
          console.log(`🗜️ MediaSelector: Compressing ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
          
          // Store original file info for filename preservation
          const originalName = file.name;
          const originalType = file.type;
          
          try {
            let compressedFile = await imageCompression(file, {
              ...DEFAULT_COMPRESSION_OPTIONS,
              quality: DEFAULT_COMPRESSION_OPTIONS.initialQuality || 0.9
            });
            
            // Apply more aggressive compression if still too large
            if (compressedFile.size > DEFAULT_COMPRESSION_OPTIONS.maxSizeMB * 1024 * 1024) {
              console.log(`🔄 MediaSelector: Applying second compression pass...`);
              compressedFile = await imageCompression(compressedFile, {
                ...DEFAULT_COMPRESSION_OPTIONS,
                maxSizeMB: DEFAULT_COMPRESSION_OPTIONS.maxSizeMB * 0.8,
                quality: DEFAULT_COMPRESSION_OPTIONS.quality * 0.9,
                maxWidthOrHeight: DEFAULT_COMPRESSION_OPTIONS.maxWidthOrHeight * 0.9
              });
            }
            
            // Final pass if still too large
            if (compressedFile.size > DEFAULT_COMPRESSION_OPTIONS.maxSizeMB * 1024 * 1024) {
              console.log(`🔄 MediaSelector: Applying final compression pass...`);
              compressedFile = await imageCompression(compressedFile, {
                ...DEFAULT_COMPRESSION_OPTIONS,
                maxSizeMB: DEFAULT_COMPRESSION_OPTIONS.maxSizeMB * 0.6,
                quality: 0.7,
                maxWidthOrHeight: Math.min(DEFAULT_COMPRESSION_OPTIONS.maxWidthOrHeight * 0.8, 1200)
              });
            }
            
            // Always create a proper File object to ensure filename preservation
            console.log(`🔄 MediaSelector: Ensuring proper File object with filename: ${originalName}`);
            console.log(`🔍 MediaSelector: Compressed file before File creation:`, {
              name: compressedFile.name,
              type: compressedFile.type,
              size: compressedFile.size,
              constructor: compressedFile.constructor.name,
              isFile: compressedFile instanceof File,
              isBlob: compressedFile instanceof Blob
            });
            
            // Force create a new File object to ensure proper filename
            compressedFile = new File([compressedFile], originalName, {
              type: originalType,
              lastModified: Date.now()
            });
            
            console.log(`🔍 MediaSelector: File after proper File creation:`, {
              name: compressedFile.name,
              type: compressedFile.type,
              size: compressedFile.size,
              constructor: compressedFile.constructor.name,
              isFile: compressedFile instanceof File,
              isBlob: compressedFile instanceof Blob,
              hasNameProperty: compressedFile.hasOwnProperty('name'),
              nameValue: compressedFile.name
            });
            
            const originalSizeMB = file.size / 1024 / 1024;
            const compressedSizeMB = compressedFile.size / 1024 / 1024;
            const reduction = ((originalSizeMB - compressedSizeMB) / originalSizeMB * 100);
            
            console.log(`✅ MediaSelector: Compression complete for ${file.name}`);
            console.log(`📊 Size: ${originalSizeMB.toFixed(2)}MB → ${compressedSizeMB.toFixed(2)}MB (${reduction.toFixed(1)}% reduction)`);
            console.log(`📁 Final filename: ${compressedFile.name} (preserved: ${compressedFile.name === originalName})`);
            
            compressedFiles.push(compressedFile);
          } catch (compressionError) {
            console.error(`❌ MediaSelector: Compression failed for ${file.name}:`, compressionError);
            compressedFiles.push(file); // Fallback to original
          }
        } else {
          compressedFiles.push(file); // Non-image files pass through
        }
      }
      
      console.log('🚀 MediaSelector: About to upload compressed files:', compressedFiles.map(f => ({
        name: f.name,
        size: f.size,
        type: f.type,
        constructor: f.constructor.name,
        hasNameProperty: f.hasOwnProperty('name'),
        nameDescriptor: Object.getOwnPropertyDescriptor(f, 'name'),
        isInstanceOfFile: f instanceof File
      })));
      
      const uploadResult = await uploadMediaFiles(compressedFiles, category);
      
      if (uploadResult.success && uploadResult.uploadedFiles.length > 0) {
        // Refresh the media list to show newly uploaded files
        await fetchMediaAssets();
        
        // Auto-select newly uploaded files if they fit within the limit
        const newIds = uploadResult.uploadedFiles.map(file => file.id);
        setSelectedItems(prev => {
          const remainingSlots = maxSelection - prev.length;
          const idsToAdd = newIds.slice(0, remainingSlots);
          return [...prev, ...idsToAdd];
        });
        
        setShowUploadSection(false);
      }
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleFileInputChange = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      await handleUploadComplete(files);
    }
    // Reset the input
    event.target.value = '';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4" style={{ zIndex: 9999 }}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden my-2 mx-2 sm:my-4 sm:mx-4 flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">{title}</h2>
              <p className="text-blue-100 mt-1">
                Selected: {selectedItems.length} / {maxSelection} images
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 p-2 rounded-lg hover:bg-white hover:bg-opacity-20"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Controls */}
        <div className="border-b border-gray-200 p-3 sm:p-4">
          <div className="flex flex-wrap gap-2 sm:gap-4 items-center justify-between">            
            <div className="flex space-x-2 sm:space-x-3">
              <Button
                type="button"
                variant={showUploadSection ? "secondary" : "primary"}
                size="sm"
                onClick={() => setShowUploadSection(!showUploadSection)}
                className="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm"
              >
                <CloudArrowUpIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">{showUploadSection ? 'Cancel Upload' : 'Upload New'}</span>
                <span className="sm:hidden">{showUploadSection ? 'Cancel' : 'Upload'}</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Upload Section */}
        {showUploadSection && (
          <div className="border-b border-gray-200 p-4 bg-gray-50">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Upload New Images to {category} category
              </label>
              <div className="flex items-center justify-center w-full">
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 hover:border-gray-400 transition-colors">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <CloudArrowUpIcon className="w-8 h-8 mb-4 text-gray-500" />
                    <p className="mb-2 text-sm text-gray-500">
                      <span className="font-semibold">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-gray-500">PNG, JPG, JPEG, WEBP (MAX. 10MB each)</p>
                  </div>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleFileInputChange}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
              </div>
              {uploading && (
                <div className="text-center text-sm text-blue-600">
                  Uploading files...
                </div>
              )}
            </div>
          </div>
        )}

        {/* Media Grid - Responsive scrollable container for all images */}
        <div className="flex-1 p-4 sm:p-6 overflow-y-auto" style={{ 
          maxHeight: 'calc(95vh - 280px)', 
          minHeight: '300px' 
        }}>
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <Loader size="large" />
            </div>
          ) : (
            <>
              {mediaAssets.length === 0 ? (
                <div className="text-center py-12">
                  <PhotoIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg">No images found</p>
                  <p className="text-gray-400 text-sm mt-2">Try uploading some images or adjust your search</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-2 sm:gap-4">
                  {mediaAssets.map((asset) => {
                    const isSelected = selectedItems.includes(asset.id);
                    const canSelect = selectedItems.length < maxSelection || isSelected;
                    
                    return (
                      <div
                        key={asset.id}
                        className={`relative group cursor-pointer transition-all duration-200 rounded-lg overflow-hidden border-2 ${
                          isSelected 
                            ? 'border-blue-500 ring-2 ring-blue-200' 
                            : canSelect 
                              ? 'border-gray-200 hover:border-blue-300' 
                              : 'border-gray-200 opacity-50 cursor-not-allowed'
                        }`}
                        onClick={() => canSelect && handleSelectItem(asset.id)}
                      >
                        {/* Image */}
                        <div className="aspect-square bg-gray-100">
                          <img
                            src={asset.thumbnail_url || asset.url}
                            alt={asset.alt_text || asset.original_filename}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <div className="w-full h-full hidden items-center justify-center bg-gray-100">
                            <PhotoIcon className="w-8 h-8 text-gray-400" />
                          </div>
                        </div>

                        {/* Selection Indicator */}
                        <div className={`absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center transition-all duration-200 ${
                          isSelected 
                            ? 'bg-blue-500 text-white' 
                            : 'bg-white bg-opacity-80 text-gray-600'
                        }`}>
                          {isSelected && <CheckIcon className="w-4 h-4" />}
                        </div>

                        {/* Image Info Overlay */}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2">
                          <p className="text-white text-xs font-medium truncate">
                            {asset.original_filename}
                          </p>
                          <p className="text-gray-300 text-xs">
                            {formatFileSize(asset.file_size_bytes)}
                          </p>
                        </div>

                        {/* Hover Overlay */}
                        {canSelect && (
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200" />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer - Always visible buttons with responsive layout */}
        <div className="border-t border-gray-200 p-3 sm:p-4 bg-gray-50 mt-auto">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            <div className="text-sm text-gray-600 order-2 sm:order-1">
              {selectedItems.length > 0 && (
                <span className="font-medium text-green-600">
                  {selectedItems.length} image{selectedItems.length !== 1 ? 's' : ''} selected
                </span>
              )}
              {selectedItems.length === maxSelection && (
                <span className="ml-2 text-amber-600">
                  (Maximum reached)
                </span>
              )}
            </div>
            
            <div className="flex space-x-3 order-1 sm:order-2 justify-end">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                className="px-4 py-2 text-sm"
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={handleConfirmSelection}
                disabled={selectedItems.length === 0}
                className="px-4 py-2 text-sm whitespace-nowrap"
              >
                Select {selectedItems.length} Image{selectedItems.length !== 1 ? 's' : ''}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaSelector;